/**
 * Login Component
 * 
 * Modern login page with left form and right container image
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Eye, EyeOff } from 'lucide-react';
import containerImage from '../assets/images/container-illustration.png'
import { authAPI } from '../services/api';
import TermsAcceptanceModal from '../components/legal/TermsAcceptanceModal';
import { storeAcceptance, type AgreementType } from '../legal';
import { setSentryUser } from '../sentry';

const Login = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState({ email: false, password: false })
  const [errorTimeout, setErrorTimeout] = useState<NodeJS.Timeout | null>(null)
  const [termsOpen, setTermsOpen] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null)
  const [requiredTypes, setRequiredTypes] = useState<AgreementType[]>([])

  // MFA challenge step. Set after /login returns requires_mfa=true.
  const [mfaChallengeToken, setMfaChallengeToken] = useState<string | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const [useRecoveryCode, setUseRecoveryCode] = useState(false)
  
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Cleanup timeout on component unmount
  React.useEffect(() => {
    return () => {
      if (errorTimeout) {
        clearTimeout(errorTimeout)
      }
    }
  }, [errorTimeout])

  // Surface Google OAuth callback errors (e.g. not_registered when user tries
  // to "Continue with Google" without having registered first).
  useEffect(() => {
    const errParam = searchParams.get('error')
    const detail   = searchParams.get('detail')
    if (!errParam) return
    const friendly = errParam === 'not_registered'
      ? (detail || 'This Google account is not registered yet. Please register with email + invite code first.')
      : (detail || 'Sign-in with Google failed. Please try again or use email + password.')
    setError(friendly)
    // Clear the params so refreshing doesn't keep re-triggering the error.
    const next = new URLSearchParams(searchParams)
    next.delete('error')
    next.delete('detail')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const validateFields = () => {
    const errors = { email: false, password: false }
    let hasErrors = false

    if (!email) {
      errors.email = true
      hasErrors = true
    }
    if (!password) {
      errors.password = true
      hasErrors = true
    }

    setFieldErrors(errors)
    return !hasErrors
  }

  // Custom setError function that prevents immediate clearing
  const setErrorWithDelay = (errorMessage: string | null) => {
    // Clear any existing timeout
    if (errorTimeout) {
      clearTimeout(errorTimeout)
      setErrorTimeout(null)
    }
    
    setError(errorMessage)
    
    // Don't auto-clear error messages, let them persist until next login attempt
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Clear any existing timeout
    if (errorTimeout) {
      clearTimeout(errorTimeout)
      setErrorTimeout(null)
    }

    setError(null)
    setFieldErrors({ email: false, password: false })

    if (!validateFields()) {
      setErrorWithDelay('Please fill in all fields')
      return
    }

    setIsLoading(true)
    await performLogin()
  }

  const finalizePostLogin = (response: {
    user?: { role?: string; company_id?: number | string; company_name?: string }
    requires_terms_acceptance?: string[]
    redirect_url?: string
  }) => {
    if (response.user) {
      localStorage.setItem('user', JSON.stringify(response.user))
      if (response.user.company_id) {
        localStorage.setItem('company_id', String(response.user.company_id))
      }
      if (response.user.company_name) {
        localStorage.setItem('company_name', response.user.company_name)
      }
      setSentryUser(response.user)
    }

    let destination = '/dispatch-console'
    if (response.user?.role === 'platform_admin') {
      destination = '/admin'
    } else if (response.redirect_url) {
      destination = response.redirect_url
    }

    const needs: AgreementType[] = (response.requires_terms_acceptance || [])
      .filter((t: string): t is AgreementType => t === 'web_tos' || t === 'privacy_policy')

    if (needs.length > 0) {
      setRequiredTypes(needs)
      setPendingNavigation(destination)
      setTermsOpen(true)
      return
    }
    navigate(destination)
  }

  const performLogin = async () => {
    try {
      const response = await authAPI.login({ email, password })

      // MFA-enabled users: switch to TOTP challenge step
      if (response.requires_mfa && response.mfa_challenge_token) {
        setMfaChallengeToken(response.mfa_challenge_token)
        setIsLoading(false)
        return
      }

      if (response.token) {
        // authAPI.login() already stored the access/refresh tokens in localStorage.
        finalizePostLogin(response)
      } else {
        setErrorWithDelay('Login failed - no token received')
      }
    } catch (err: unknown) {
      // Handle errors primarily by HTTP status code
      let errorMessage = 'Login failed. Please try again.'
      const axiosErr = err as { response?: { status?: number; data?: { lockoutEndTime?: string; message?: string; warning?: string } }; message?: string }
      const status = axiosErr.response?.status
      const data = axiosErr.response?.data

      if (status === 429) {
        // Rate limited - too many attempts
        if (data?.lockoutEndTime) {
          const lockoutTime = new Date(data.lockoutEndTime).toLocaleTimeString()
          errorMessage = `Too many failed login attempts. Your account is temporarily locked. Please try again after ${lockoutTime}.`
        } else {
          errorMessage = data?.message || 'Too many failed login attempts. Please try again later.'
        }
        setFieldErrors({ email: true, password: true })
      } else if (status === 401) {
        // Invalid credentials or unverified email
        errorMessage = 'Incorrect email or password. Please check your credentials and try again.'
        if (data?.warning) {
          errorMessage += ` (${data.warning})`
        }
        // If the backend says email not verified, show that instead
        if (data?.message?.includes('verify your email')) {
          errorMessage = 'Please verify your email address before logging in. Check your inbox for a verification link.'
        } else {
          setFieldErrors({ email: true, password: true })
        }
      } else if (status === 403) {
        // Account restricted
        errorMessage = data?.message || 'Your account has been restricted. Please contact support.'
      } else if (status && status >= 500) {
        errorMessage = 'Server error. Please try again later.'
      } else if (axiosErr.message === 'Network Error') {
        errorMessage = 'Network error. Please check your internet connection and try again.'
      } else if (data?.message) {
        errorMessage = data.message
      }

      setErrorWithDelay(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!mfaChallengeToken) return
    const trimmed = mfaCode.trim()
    if (!trimmed) {
      setErrorWithDelay(useRecoveryCode ? 'Enter your recovery code' : 'Enter the 6-digit code from your authenticator app')
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const payload = useRecoveryCode
        ? { mfa_challenge_token: mfaChallengeToken, recovery_code: trimmed }
        : { mfa_challenge_token: mfaChallengeToken, token: trimmed }
      const response = await authAPI.mfaVerify(payload)
      finalizePostLogin(response)
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } }
      const status = axiosErr.response?.status
      if (status === 401) {
        const msg = axiosErr.response?.data?.message || 'Invalid code'
        // Challenge token expired (5 min): kick back to password step
        if (msg.toLowerCase().includes('expired')) {
          setMfaChallengeToken(null)
          setMfaCode('')
          setErrorWithDelay('MFA session expired. Please log in again.')
        } else {
          setErrorWithDelay(msg)
        }
      } else if (status === 429) {
        setErrorWithDelay('Too many MFA attempts. Please wait a few minutes and try again.')
      } else {
        setErrorWithDelay('MFA verification failed. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const cancelMfa = () => {
    setMfaChallengeToken(null)
    setMfaCode('')
    setUseRecoveryCode(false)
    setError(null)
  }

  const handleGoogleSignIn = () => {
    // Strict login: backend refuses to auto-create users when intent='login'
    // — unknown emails are redirected back to /login?error=not_registered.
    // Post-OAuth, the dashboard's /auth/me bootstrap (TermsGate in App)
    // surfaces the same modal if the user hasn't accepted current versions.
    authAPI.googleAuth({ intent: 'login' })
  }

  const handleTermsAccept = () => {
    setTermsOpen(false)
    storeAcceptance(requiredTypes)
    if (pendingNavigation) {
      navigate(pendingNavigation)
      setPendingNavigation(null)
    }
  }

  const handleTermsDecline = () => {
    setTermsOpen(false)
    setRequiredTypes([])
    setPendingNavigation(null)
    // Decline = revoke session; user must accept to use the app.
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('company_id')
    localStorage.removeItem('company_name')
    setErrorWithDelay('You must accept the Terms of Service and Privacy Policy to sign in.')
  }

  return (
    <div className="min-h-screen flex relative">
      {/* Logo - Top Left */}
      <div className="absolute top-6 left-6 z-10">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="EAloom" className="w-7 h-7 rounded-md" />
          <h1 className="text-lg font-semibold text-gray-900">EAloom</h1>
        </div>
      </div>

      {/* Copyright - Bottom Left */}
      <div className="absolute bottom-6 left-6 z-10">
        <p className="text-xs text-gray-500 leading-relaxed">© 2026 EAloom. All rights reserved.</p>
        <p className="text-xs text-gray-400 leading-relaxed">EAloom is owned and operated by Lambda Flow LLC.</p>
      </div>

      {/* Left side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-6 lg:px-20 xl:px-28">
        <div className="w-full max-w-sm">

          {/* Welcome text */}
          <div className="mb-8 mt-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {mfaChallengeToken ? 'Two-factor verification' : 'Welcome back'}
            </h2>
            <p className="text-gray-600">
              {mfaChallengeToken
                ? (useRecoveryCode
                    ? 'Enter one of your recovery codes (e.g. ABCD-EFGH-JKMN).'
                    : 'Enter the 6-digit code from your authenticator app.')
                : 'Welcome back! Please enter your details.'}
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* MFA challenge form (shown only when /login returned requires_mfa) */}
          {mfaChallengeToken && (
            <form onSubmit={handleMfaSubmit} className="space-y-6">
              <div>
                <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700 mb-2">
                  {useRecoveryCode ? 'Recovery code' : 'Authentication code'}
                </label>
                <Input
                  id="mfa-code"
                  name="mfa-code"
                  type="text"
                  autoComplete="one-time-code"
                  inputMode={useRecoveryCode ? 'text' : 'numeric'}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder={useRecoveryCode ? 'ABCD-EFGH-JKMN' : '123456'}
                  maxLength={useRecoveryCode ? 14 : 6}
                  className="w-full h-11 px-3 border-2 border-gray-300 focus:border-green-500 rounded-lg tracking-widest text-center text-lg"
                  required
                  autoFocus
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg disabled:opacity-50"
              >
                {isLoading ? 'Verifying...' : 'Verify'}
              </Button>

              <div className="flex flex-col items-center gap-2 text-sm">
                <button
                  type="button"
                  onClick={() => { setUseRecoveryCode(!useRecoveryCode); setMfaCode(''); setError(null); }}
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  {useRecoveryCode ? 'Use authenticator app instead' : 'Use a recovery code instead'}
                </button>
                <button
                  type="button"
                  onClick={cancelMfa}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Cancel and sign in as a different user
                </button>
              </div>
            </form>
          )}

          {/* Login form */}
          {!mfaChallengeToken && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className={`relative rounded-lg border-2 transition-colors ${
                fieldErrors.email 
                  ? 'border-red-300 focus-within:border-red-500' 
                  : 'border-gray-300 focus-within:border-green-500'
              }`}>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full h-11 px-3 border-0 bg-transparent focus:ring-0 focus:outline-none rounded-lg"
                  required
                />
              </div>
              {fieldErrors.email && (
                <p className="text-xs text-red-600 mt-1">Email is required</p>
              )}
            </div>

            {/* Password field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className={`relative rounded-lg border-2 transition-colors ${
                fieldErrors.password 
                  ? 'border-red-300 focus-within:border-red-500' 
                  : 'border-gray-300 focus-within:border-green-500'
              }`}>
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-11 px-3 pr-10 border-0 bg-transparent focus:ring-0 focus:outline-none rounded-lg"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="text-xs text-red-600 mt-1">Password is required</p>
              )}
            </div>

            {/* Remember me and Forgot password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                  className="w-4 h-4 border-gray-300"
                />
                <label htmlFor="remember" className="ml-2 text-sm text-gray-700">
                  Remember for 30 days
                </label>
              </div>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-green-600 hover:text-green-700 font-medium"
              >
                Forgot password
              </button>
            </div>

            {/* Sign in button */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>

            {/* Divider with OR */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">or</span>
              </div>
            </div>

            {/* Google sign in */}
            <Button
              type="button"
              onClick={handleGoogleSignIn}
              variant="outline"
              className="w-full h-11 border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium rounded-lg transition-colors duration-200"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in with Google
            </Button>

            {/* Sign up link */}
            <div className="text-center">
              <span className="text-sm text-gray-600">Don't have an account? </span>
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="text-sm text-green-600 hover:text-green-700 font-medium"
              >
                Sign up
              </button>
            </div>

            {/* Support link — visitors who can't log in still need a way to reach us */}
            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => navigate('/support')}
                className="text-xs text-gray-500 hover:text-green-600 transition-colors"
              >
                Need help? Contact support →
              </button>
            </div>
          </form>
          )}
        </div>
      </div>

      {/* Right side - Container image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden">
        {/* Container illustration */}
        <div className="flex items-center justify-center w-full h-full relative">
          <img 
            src={containerImage} 
            alt="Container Illustration" 
            className="w-full h-full max-w-none max-h-none object-contain scale-150"
          />
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-32 left-20 w-24 h-24 bg-blue-200/20 rounded-full blur-lg"></div>
      </div>

      <TermsAcceptanceModal
        open={termsOpen}
        agreementTypes={requiredTypes}
        onAccept={handleTermsAccept}
        onDecline={handleTermsDecline}
        blocking={true}
      />
    </div>
  )
}

export default Login